import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { styled, createTheme } from '@mui/material/styles';
import LogoutIcon from '@mui/icons-material/Logout';
import DashboardIcon from '@mui/icons-material/Dashboard';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import BarChartIcon from '@mui/icons-material/BarChart';
import DescriptionIcon from '@mui/icons-material/Description';
import LayersIcon from '@mui/icons-material/Layers';
import { AppProvider } from '@toolpad/core/AppProvider';
import { DashboardLayout } from '@toolpad/core/DashboardLayout';
import { PageContainer } from '@toolpad/core/PageContainer';
import Grid from '@mui/material/Grid';
import { createCategory, updateCategory, deleteProductCategory, getAllCategories, uploadImageToMedia, getCategoryBySlug } from '../Api';
import { BsGrid3X3GapFill } from "react-icons/bs";
import { HiUsers } from 'react-icons/hi2';
import { FaBasketShopping, FaFilePrescription } from "react-icons/fa6";
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import Pagination from '@mui/material/Pagination';
import Stack from '@mui/material/Stack';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import Modal from '@mui/material/Modal';
import { toast } from 'react-toastify';
import SearchIcon from '@mui/icons-material/Search';
import { FaFileAlt, FaLocationArrow } from "react-icons/fa";
import { IconButton, InputAdornment, TextField } from '@mui/material';
import { BiSolidCoupon } from 'react-icons/bi';

const NAVIGATION = [
  {
    kind: 'header',
    title: 'Main items',
  },
  {
    segment: 'dashboard',
    title: 'Dashboard',
    icon: <DashboardIcon />,
  },
  {
    segment: 'dashboard-products',
    title: 'Products',
    icon: <FaBasketShopping style={{ fontSize: '22px' }} />,
  },
  {
    segment: 'dashboard-users',
    title: 'Users',
    icon: <HiUsers style={{ fontSize: '22px' }} />,
  },
  {
    segment: 'dashboard-categories',
    title: 'Categories',
    icon: <BsGrid3X3GapFill style={{ fontSize: '22px' }} />,
  },
  {
    segment: 'dashboard-orders',
    title: 'Orders',
    icon: <ShoppingCartIcon />,
  },
  {
    segment: 'prescriptions',
    title: 'Prescriptions',
    icon: <FaFilePrescription style={{ fontSize: '20px' }} />,
  },
  {
    segment: 'dashboard-coupons',
    title: 'Coupons',
    icon: <BiSolidCoupon style={{ fontSize: '22px' }} />,
  },
  {
    segment: 'dashboard-content',
    title: 'Content',
    icon: <FaFileAlt style={{ fontSize: '20px' }} />,
  },
  {
    segment: 'dashboard-location',
    title: 'Location',
    icon: <FaLocationArrow />,
  },
  {
    kind: 'divider',
  },
  {
    kind: 'header',
    title: 'Analytics',
  },
  {
    segment: 'reports',
    title: 'Reports',
    icon: <BarChartIcon />,
    children: [
      {
        segment: 'sales',
        title: 'Sales',
        icon: <DescriptionIcon />,
      },
      {
        segment: 'traffic',
        title: 'Traffic',
        icon: <DescriptionIcon />,
      },
    ],
  },
  {
    segment: 'integrations',
    title: 'Integrations',
    icon: <LayersIcon />,
  },
];

const Skeleton = styled('div')(({ theme, height }) => ({
  backgroundColor: theme.palette.action.hover,
  borderRadius: theme.shape.borderRadius,
  height,
  content: '" "',
}));

const style = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: 400,
  height: 400,
  overflowY: "scroll",
  bgcolor: "background.paper",
  border: "1px solid transparent",
  borderRadius: 1,
  shadow: 24,
  p: 4,
};

function useDemoRouter(initialPath) {
  const [pathname, setPathname] = React.useState(initialPath);
  const navigate = useNavigate();

  const router = React.useMemo(() => {
    return {
      pathname,
      searchParams: new URLSearchParams(),
      navigate: (path) => {
        setPathname(String(path));
        navigate(path);
      },
    };
  }, [pathname, navigate]);

  return router;
}

// Inline placeholder for a category image that fails to load. A data URI rather
// than a file path so it can never itself 404 — which is exactly how the previous
// fallback turned one broken image into an endless onError loop (BUG-7).
const CATEGORY_IMAGE_FALLBACK =
  'data:image/svg+xml;utf8,' +
  encodeURIComponent(
    '<svg xmlns="http://www.w3.org/2000/svg" width="100" height="100">' +
    '<rect width="100" height="100" fill="#f3f4f6"/>' +
    '<text x="50" y="54" font-family="sans-serif" font-size="11" fill="#9ca3af" ' +
    'text-anchor="middle">No image</text></svg>'
  );

const theme = createTheme({
  palette: {
    mode: 'light',
  },
});

const ITEMS_PER_PAGE = 50;

function DshCategories({ onLogout }) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const router = useDemoRouter('/dashboard-categories');
  const [categories, setCategories] = useState([]);
  const [open, setOpen] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [currentCategoryId, setCurrentCategoryId] = useState(null);
  const [categoryData, setCategoryData] = useState({
    name: '',
    image: null,
    existingImageId: null,
  });
  const [previewImage, setPreviewImage] = useState(null);
  const [openLogoutDialog, setOpenLogoutDialog] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredCategories, setFilteredCategories] = useState([]);

  const handleOpen = () => setOpen(true);
  const handleClose = () => {
    setOpen(false);
    setIsEditMode(false);
    setCurrentCategoryId(null);
    setCategoryData({ name: '', image: null, existingImageId: null });
    setPreviewImage(null);
  };

  useEffect(() => {
    async function fetchCategories() {
      setLoading(true);
      try {
        const response = await getAllCategories(currentPage, ITEMS_PER_PAGE);
        console.log('Fetched categories:', response); // Debug full response
        setCategories(Array.isArray(response.data) ? response.data : []);
        setFilteredCategories(Array.isArray(response.data) ? response.data : []);
        setTotalPages(Math.ceil(response.total / ITEMS_PER_PAGE));
      } catch (error) {
        console.error('Error fetching categories:', error);
        toast.error(error.message || 'Failed to load categories');
        setCategories([]);
        setTotalPages(1);
      } finally {
        setLoading(false);
      }
    }
    fetchCategories();
  }, [currentPage]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setCategoryData(prev => ({ ...prev, [name]: value }));
  };

  const handleImageChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const maxSize = 5 * 1024 * 1024; // 5MB
    const allowedTypes = ["image/jpeg", "image/png", "image/gif"];

    try {
      const mimeType = file.type;
      if (!allowedTypes.includes(mimeType)) {
        throw new Error(`Unsupported file type: ${mimeType}. Please upload JPEG, PNG, or GIF.`);
      }

      const isValidImage = await new Promise((resolve) => {
        const img = new Image();
        img.onload = () => resolve(true);
        img.onerror = () => resolve(false);
        img.src = URL.createObjectURL(file);
      });

      if (!isValidImage) {
        throw new Error(`File ${file.name} is not a valid image`);
      }

      if (file.size > maxSize) {
        throw new Error(`File ${file.name} exceeds 5MB limit`);
      }

      setCategoryData(prev => ({ ...prev, image: file }));
      setPreviewImage(URL.createObjectURL(file));
    } catch (err) {
      console.error(`Error processing ${file.name}:`, err);
      toast.error(err.message);
    }
  };

  const handleLogoutClick = () => {
    setOpenLogoutDialog(true);
  };

  const handleLogoutConfirm = () => {
    setOpenLogoutDialog(false);
    onLogout();
    navigate('/');
  };

  const handleLogoutCancel = () => {
    setOpenLogoutDialog(false);
  };

  const handleSearch = async () => {
    setLoading(true)
    try {
      const response = await getCategoryBySlug(searchTerm)
      console.log(response);

      setFilteredCategories(response ? response : [])
    } catch (err) {
      console.log(err)
    } finally {
      setLoading(false)
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!categoryData.name.trim()) {
      toast.error("Category name is required and cannot be empty");
      return;
    }

    setLoading(true);
    try {
      let payload = { name: categoryData.name.trim() };

      if (categoryData.image instanceof File) {
        if (!categoryData.image.name) {
          throw new Error("Image file name is missing");
        }
        const uploadResponse = await uploadImageToMedia(
          categoryData.image,
          "categories",
          categoryData.image.name
        );
        console.log('Image upload response:', uploadResponse); // Debug
        if (uploadResponse?.id) {
          payload.image_id = uploadResponse.id; // Send image_id
          toast.success("Category image uploaded successfully");
        } else {
          throw new Error("Image upload response did not return an ID");
        }
      } else if (isEditMode && categoryData.existingImageId) {
        payload.image_id = categoryData.existingImageId; // Retain existing image_id
      }

      console.log('Payload sent to updateCategory:', payload); // Debug payload

      let response;

      if (isEditMode) {
        response = await updateCategory(currentCategoryId, payload);
        toast.success("Category updated successfully");
      } else {
        response = await createCategory(payload);
        toast.success("Category created successfully");
      }

      const categoriesResponse = await getAllCategories(currentPage, ITEMS_PER_PAGE);
      setCategories(Array.isArray(categoriesResponse.data) ? categoriesResponse.data : []);
      setTotalPages(Math.ceil(categoriesResponse.total / ITEMS_PER_PAGE));

      handleClose();
    } catch (error) {
      console.error(`Error ${isEditMode ? "updating" : "creating"} category:`, {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
      });
      toast.error(
        error.response?.data?.message ||
        error.message ||
        (isEditMode ? "Failed to update category" : "Failed to create category")
      );
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (category) => {
    setIsEditMode(true);
    setCurrentCategoryId(category.id);
    setCategoryData({
      name: category.name,
      image: null,
      existingImageId: category.image?.id || null,
    });
    setPreviewImage(category.image?.src || null);
    setOpen(true);
  };

  async function deleteCategory(id) {
    if (window.confirm("Are you sure you want to delete this category?")) {
      setLoading(true);
      try {
        await deleteProductCategory(id);
        toast.success("Category deleted successfully");
        const response = await getAllCategories(currentPage, ITEMS_PER_PAGE);
        console.log('Fetched categories after delete:', response); // Debug
        setCategories(Array.isArray(response.data) ? response.data : []);
        setTotalPages(Math.ceil(response.total / ITEMS_PER_PAGE));
        if (response.data.length === 0 && currentPage > 1) {
          setCurrentPage(prev => prev - 1);
        }
      } catch (error) {
        console.error('Error deleting category:', error);
        toast.error(error.message || "Failed to delete category");
      } finally {
        setLoading(false);
      }
    }
  }

  const handlePageChange = (event, value) => {
    setCurrentPage(value);
  };

  const branding = {
    logo: (
      <div className="header-logo">
        <img onClick={() => navigate('/')} src={require('../images/logo.png')} alt="" style={{ height: '52px', width: '60px' }} />
      </div>
    ),
    title: '',
  };

  const ToolbarActions = () => (
    <Button
      variant="outlined"
      startIcon={<LogoutIcon />}
      onClick={handleLogoutClick}
      sx={{ marginRight: 2 }}
      style={{ background: "#0165AB", color: "#fff" }}
    >
      Logout
    </Button>
  );

  return (
    <AppProvider
      navigation={NAVIGATION}
      router={router}
      theme={theme}
      branding={branding}
    >
      <DashboardLayout
        slots={{
          toolbarActions: ToolbarActions,
        }}
      >
        <PageContainer>
          {loading ? (
            <Grid container spacing={1}>
              <Grid size={5} />
              <Grid size={10}>
                <Skeleton height={35} />
              </Grid>
              <Grid size={2}>
                <Skeleton height={35} />
              </Grid>
              <Grid size={10}></Grid>
              <Grid size={2}></Grid>
              {[...Array(8)].map((_, index) => (
                <Grid size={3} key={index}>
                  <Skeleton height={220} />
                </Grid>
              ))}
            </Grid>
          ) : (
            <section className="admin-dashboard">
              <Grid container spacing={1}>
                <Grid size={4}>
                  <div style={{ height: "40px" }}>
                    <TextField
                      id="search-field"
                      type="search"
                      style={{ width: "100%" }}
                      placeholder="Search..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      onKeyDown={(e) => { if (e.key === 'Enter') handleSearch(); }}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton aria-label="search" onClick={handleSearch}>
                              <SearchIcon />
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                    />
                  </div>
                </Grid>
                <Grid size={6}></Grid>
                <Grid size={2}>
                  <button
                    style={{
                      height: "35px",
                      width: "100%",
                      border: "1px solid transparent",
                      background: "#0165AB",
                      color: "#fff",
                      borderRadius: "5px",
                      cursor: "pointer",
                    }}
                    onClick={handleOpen}
                  >
                    ADD CATEGORY
                  </button>

                  <Modal
                    open={open}
                    aria-labelledby="modal-modal-title"
                    aria-describedby="modal-modal-description"
                  >
                    <Box sx={style}>
                      <Typography id="modal-modal-title" variant="h6" component="h2">
                        {isEditMode ? 'Edit Category' : 'Add Category'}
                      </Typography>
                      <Typography id="modal-modal-description" sx={{ mt: 2 }}>
                        <form onSubmit={handleSubmit}>
                          <div className="product-input-group">
                            <label htmlFor="name">Name</label>
                            <br />
                            <input
                              onChange={handleInputChange}
                              type="text"
                              name="name"
                              required
                              value={categoryData.name}
                            />
                          </div>
                          <div className="product-input-group">
                            <div className="d-flex">
                              <label htmlFor="image">Image</label>
                              <input
                                type="file"
                                name="image"
                                id="image"
                                accept="image/jpeg,image/png,image/gif"
                                onChange={handleImageChange}
                              />
                            </div>
                            <small style={{ color: "#666", marginTop: "5px", display: "block" }}>
                              Supported formats: JPEG, PNG, GIF. Maximum file size: 5MB.
                            </small>
                            <div
                              className="image-preview"
                              style={{ display: "flex", flexWrap: "wrap", gap: "10px", marginTop: "10px" }}
                            >
                              {previewImage ? (
                                <img
                                  src={previewImage}
                                  alt="Preview"
                                  style={{ width: "100px", height: "100px", objectFit: "cover" }}
                                />
                              ) : (
                                <p>No image selected</p>
                              )}
                            </div>
                          </div>
                          <div className="d-flex">
                            <button
                              type="button"
                              style={{
                                height: "35px",
                                width: "100px",
                                border: "1px solid transparent",
                                background: "red",
                                color: "#fff",
                                borderRadius: "5px",
                                cursor: "pointer",
                              }}
                              onClick={handleClose}
                            >
                              Cancel
                            </button>
                            <button
                              type="submit"
                              style={{
                                height: "35px",
                                width: "100px",
                                margin: "0 0 0 20px",
                                border: "1px solid transparent",
                                background: "#0165AB",
                                color: "#fff",
                                borderRadius: "5px",
                                cursor: "pointer",
                              }}
                              disabled={loading}
                            >
                              {isEditMode ? 'Update' : 'Add'}
                            </button>
                          </div>
                        </form>
                      </Typography>
                    </Box>
                  </Modal>
                </Grid>

                <Grid size={12} height={5}></Grid>
                {filteredCategories.length > 0 ? (
                  filteredCategories.map((item) => (
                    <Grid size={3} key={item.id}>
                      <div style={{ height: "fit-content", boxShadow: "0 0 10px 0 #e7e7e7", borderRadius: "5px", padding: "10px 0", textAlign: "center" }}>
                        <div style={{ height: "100px" }}>
                          {item?.image?.src ? (
                            <img
                              style={{ height: "100px", width: "100px", objectFit: "cover" }}
                              src={`${item.image.src}?t=${new Date().getTime()}`}
                              alt={item.slug || 'category'}
                              onError={(e) => {
                                // BUG-7: this pointed at the literal string
                                // '/path/to/fallback-image.png', which 404s — and
                                // because the replacement also fails to load, it
                                // re-fired onError against the new src, looping.
                                // Clearing the handler first stops the loop; the
                                // placeholder is an inline SVG so it cannot 404.
                                console.error(`Failed to load image for category ${item.id}: ${item.image.src}`);
                                e.target.onerror = null;
                                e.target.src = CATEGORY_IMAGE_FALLBACK;
                              }}
                            />
                          ) : (
                            <div style={{ height: "100px", width: "100px", display: "flex", alignItems: "center", justifyContent: "center" }}>
                              <span>No Image</span>
                            </div>
                          )}
                        </div>
                        <h3>{item?.name}</h3>
                        <button
                          style={{
                            width: "180px",
                            height: "fit-content",
                            padding: "8px 0",
                            border: "1px solid transparent",
                            background: "#0165AB",
                            color: "#fff",
                            margin: "5px auto 0",
                            display: "block",
                            borderRadius: "5px",
                            cursor: "pointer",
                            textTransform: "uppercase",
                            fontSize: "12px",
                            fontWeight: "600",
                          }}
                          onClick={() => handleEdit(item)}
                          disabled={loading}
                        >
                          Edit
                        </button>
                        <button
                          style={{
                            width: "180px",
                            height: "fit-content",
                            padding: "8px 0",
                            border: "1px solid transparent",
                            background: "#0165AB",
                            color: "#fff",
                            margin: "5px auto 0",
                            display: "block",
                            borderRadius: "5px",
                            cursor: "pointer",
                            textTransform: "uppercase",
                            fontSize: "12px",
                            fontWeight: "600",
                          }}
                          onClick={() => deleteCategory(item.id)}
                          disabled={loading}
                        >
                          Delete
                        </button>
                      </div>
                    </Grid>
                  ))
                ) : (
                  <Grid size={12}>
                    <h4 style={{ textAlign: 'center' }}>Categories not found!</h4>
                  </Grid>
                )}
              </Grid>
              <Stack spacing={2} style={{ margin: '5% auto 0', display: 'flex', alignItems: 'center' }}>
                <Pagination
                  count={totalPages}
                  page={currentPage}
                  onChange={handlePageChange}
                  color="primary"
                />
              </Stack>
            </section>
          )}
        </PageContainer>
      </DashboardLayout>
      <Dialog
        open={openLogoutDialog}
        onClose={handleLogoutCancel}
        aria-labelledby="logout-dialog-title"
        aria-describedby="logout-dialog-description"
      >
        <DialogTitle id="logout-dialog-title">Confirm Logout</DialogTitle>
        <DialogContent>
          <DialogContentText id="logout-dialog-description">
            Are you sure you want to log out?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleLogoutCancel} disabled={loading}>Cancel</Button>
          <Button onClick={handleLogoutConfirm} autoFocus disabled={loading}>
            Logout
          </Button>
        </DialogActions>
      </Dialog>
    </AppProvider>
  );
}

export default DshCategories;